import { FieldFunctionOptions } from '@apollo/client';

import { MessagesQuery } from '../../types/api.graphql';

type MessagesType = MessagesQuery['messages'];

export const mergeMessagesCache = (
  existing: MessagesType = undefined,
  incoming: MessagesType,
  { readField }: FieldFunctionOptions,
) => {
  if (!incoming?.nodes) {
    return existing;
  }
  let existingNodes = existing?.nodes || [];

  if (incoming.nodes.length === 1) {
    const newNode = incoming?.nodes?.[0];
    // Always replace messages by sentAt when coming from subscription or new local message
    existingNodes = existingNodes.filter(
      (existingNode) =>
        readField<string>('sentAt', existingNode).toString() !== readField<string>('sentAt', newNode).toString(),
    );
    return {
      ...existing,
      ...incoming,
      nodes: [...existingNodes, newNode],
    };
  }

  // Ignore incoming nodes that already exists
  // eslint-disable-next-line no-unsafe-optional-chaining
  const newNodes = [...incoming?.nodes].reverse().filter((newNode: any) => {
    return !existingNodes.find(
      (oldNode: any) =>
        readField<string>('id', newNode)?.toString() !== '-1' &&
        (readField<string>('id', newNode)?.toString() === readField<string>('id', oldNode)?.toString() ||
          (readField<string>('sentAt', newNode) &&
            readField<string>('sentAt', newNode)?.toString() === readField<string>('sentAt', oldNode)?.toString())),
    );
  });

  if (newNodes.length === 0) {
    // If there is only one item without pageInfo
    // we can consider it is a new message comming from a subscription or the user input
    // so we add it to the bottom of the list
    return {
      ...existing,
      pageInfo: incoming.pageInfo,
    };
  }

  if (newNodes.length === 1) {
    const insertObjectOrderedBySentAt = (array, newObject) => {
      const indexToInsert = array.findIndex(
        (item) => new Date(readField('sentAt', newObject)) < new Date(readField('sentAt', item)),
      );
      const insertIndex = indexToInsert !== -1 ? indexToInsert : array.length;
      return [...array.slice(0, insertIndex), newObject, ...array.slice(insertIndex)];
    };

    return {
      ...existing,
      ...incoming,
      nodes: insertObjectOrderedBySentAt(existingNodes, newNodes[0]),
      pageInfo: incoming.pageInfo,
      totalCount: Number(existing.totalCount) + 1,
    };
  }

  const newData = {
    ...existing,
    ...incoming,
    nodes: [...newNodes, ...existingNodes],
  };
  return newData;
};
